// A customer's section on the work board: colour-coded header with the outstanding
// money, then the client's tasks — open work, and completed work struck through
// beneath it. Leads use their own compact LeadRow on the Leads screen; both screens
// share TaskList, so a task behaves identically wherever it appears.
//
// One way to add a task: the button opens the full form (title, urgency, due date,
// assignee). There is no inline quick-add — two paths meant two different sets of
// fields, and the short one always won by being closer to hand.
//
// The group folds, but only the TASKS do. The header — the client, the urgency mark,
// the owed/overdue money, the add button — stays put whether folded or not, so
// closing a group never hides a number or takes an action out of reach. Board owns
// the open/closed state, because remembering it and the collapse-all control both
// need to see every group at once.
import he from '../i18n/he';
import { formatMoney } from './Money';
import Button from './Button';
import TaskList, { openTasks } from './TaskList';
import UrgencyBadge, { topUrgency } from './UrgencyBadge';
import { taskClient } from '../utils/taskClient';

export default function ClientGroup({
  group, collapsed, onToggle, onChanged, onEdit, onAdd,
  editingNoteId, onStartNote, onStopNote,
}) {
  // The internal group (tasks with no client) arrives with `client: null`; it is headed
  // "פנימי" and adds a task with no client.
  const internal = group.kind === 'internal';
  const client = taskClient(group.client);
  const { money, tasks } = group;
  const open = openTasks(tasks);

  return (
    <div className="client-group card" style={{ borderInlineStartColor: client.color || 'var(--border)' }}>
      <div className="group-header">
        <button
          type="button"
          className="group-toggle"
          onClick={onToggle}
          aria-expanded={!collapsed}
        >
          <span className="chevron" aria-hidden="true">{collapsed ? '▸' : '▾'}</span>
          <span className="swatch" style={{ background: client.color || 'var(--muted)' }} />
          <h2>{client.name}</h2>
          {/* Folded, the count says how much work is behind the fold and the badge
              keeps a burning task from hiding there — same rule as a collapsed lead. */}
          {collapsed && open.length > 0 && (
            <>
              <span className="group-count">{open.length}</span>
              <UrgencyBadge value={topUrgency(open)} />
            </>
          )}
        </button>
        <div className="group-meta">
          {/* Overdue is loud (red badge); owed is quiet text — the eye lands on late first. */}
          {money.overdue > 0 && (
            <span className="badge red">{he.board.overdue} {formatMoney(money.overdue)}</span>
          )}
          {money.owed > 0 && (
            <span className="owed-quiet">{he.board.owed} {formatMoney(money.owed)}</span>
          )}
          {onAdd && <Button small variant="ghost" onClick={() => onAdd(internal ? null : client)}>{he.board.addTask}</Button>}
        </div>
      </div>

      {!collapsed && (
        <TaskList
          tasks={tasks}
          emptyText={internal ? he.board.emptyInternalTasks : he.board.emptyGroupTasks}
          onEdit={onEdit}
          onChanged={onChanged}
          editingNoteId={editingNoteId}
          onStartNote={onStartNote}
          onStopNote={onStopNote}
        />
      )}
    </div>
  );
}
